import { Container } from "@chakra-ui/react";
import getConfig from 'next/config';
import { GetServerSideProps } from "next";
import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { getPackLayout } from 'layouts';
import { CreatorCard } from 'domains/creator-card';
import { ItemDnD } from "domains/drag-and-drop";
import { Ad, PackTitle, LoginPrompt, AddAssetFooter, DraftAssetWrapper, CreatePassword, PromptForPassword, PromptForOldPassword } from "domains/pack";
import { AssetSelectionProvider } from "domains/interactions";
import { CustomNextPage, RoleLevels} from 'lib/types';
import { GetUserResponseData } from "lib/clients/client-user/types/UserClient";
import { GetPackResponseData } from "lib/clients/client-packs/types/PacksClient";
import { teamAppUrl, isReadOnly, needOldPassword } from "lib/utils";
import { useXdStorageContext } from "lib/storage";
import { useEffect } from "react";
import { useE2EEContext } from "lib/e2ee";
import { useStickyAd, usePack, useLoggedinUser, useHandlePastedAsset } from "lib/hooks";
import { useRouter } from "next/router";
import { useCookies } from "react-cookie";

const { publicRuntimeConfig: { genericSubDomain, baseDomain, basePort, baseRESTPort, baseProtocol } } = getConfig();

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  let host = ctx.req.headers.host;
  host = host.replace(/(^\w+:|^)\/\//, '');

  const requestBaseUrl = host.replace(basePort || '80', baseRESTPort || '443');

  const config: AxiosRequestConfig = {
    headers: {
      Cookie: ctx.req.headers.cookie,
    },
    baseURL: `${baseProtocol || 'http'}://${requestBaseUrl}/v1/`,
    withCredentials: true,
  };

  try {
    const packId: string = ctx.query.id as string;
    const { data: pack } = await Axios.get<never, AxiosResponse<GetPackResponseData>>(`packs/${packId}`, config);

    if (packId.startsWith('x')) {
      try {
        const { data: user } = await Axios.get<never, AxiosResponse<GetUserResponseData>>('user', config);

        // User owns the shared pack they are trying to view
        if (user.id === pack.user.id) {
          return {
            redirect: {
              permanent: false,
              destination: `/packs/${pack.base_id}`,
            }
          }
        }
      } catch (err) {
      }
    }

    if (!pack.index_enabled) {
      ctx.res.setHeader('X-Robots-Tag', 'noindex, nofollow, noarchive, nosnippet');
    }

    return { props: { prefetchedPack: pack } };
  } catch (err: unknown) {
    if (Axios.isAxiosError(err)) {
      const status: number = err.response?.status;

      if (status === 306) {
        const { data: teams } = await Axios.get<never, AxiosResponse>('user/teams', config);

        if (teams.length === 1) {
          const teamUrl = teamAppUrl(teams[0].identifier);

          return {
            redirect: {
              permanent: false,
              destination: `${teamUrl}/packs/${ctx.query.id}`
            }
          }
        }

        return {
          redirect: {
            permanent: false,
            destination: `${baseProtocol}://${genericSubDomain}${baseDomain}${basePort}/teams?next=/packs/${ctx.query.id}`
          }
        }
      }

      // TODO: Implement 401 page
      if (status === 401) {
        return {
          redirect: {
            permanent: false,
            destination: `/packs`,
          }
        }
      }

      if (status === 403) {
        return {
          redirect: {
            permanent: false,
            destination: `/pack-forbidden`,
          }
        }
      }

      if (status === 404) {
        return {
          redirect: {
            permanent: false,
            destination: `/pack-not-found`,
          }
        }
      }
    }

    ctx.res.setHeader('X-Robots-Tag', 'noindex, nofollow, noarchive, nosnippet');
    return { props: { prefetchedPack: null } }
  }
}

const SinglePack: CustomNextPage<{ prefetchedPack: GetPackResponseData }> = ({ prefetchedPack }) => {
  const { query: { id }} = useRouter();
  const packId = id as string;
  const { data: pack, isLoading } = usePack(packId as string);
  //We are turning ads off here until either it is done better of excised completely
  const displayAd = false;
  //const { displayAd } = useStickyAd(packId);
  const isPackReadOnly = isReadOnly(pack);
  const { setItem } = useXdStorageContext();
  const { needNonce, needPassword } = useE2EEContext();
  const { data: user } = useLoggedinUser();
  const [ _, setCookie ] = useCookies(['packs_last_visited_share']);

  useHandlePastedAsset();

  useEffect(() => {
    if (!pack?.anon || !pack?.id.startsWith('x')) return;

    const hash = window.location.hash?.slice(1);

    setCookie('packs_last_visited_share', `${pack.id}#${hash}`, { path: '/', domain: baseDomain});
  }, [pack, setItem, setCookie]);

  if (isLoading) {
    return null;
  }

  if (pack.is_encrypted) {
    if (needPassword) {
      return (
        <Container maxWidth={920} width="100%" padding={["0 10px", "40px 0"]} margin={'0 auto'}>
          <PackTitle />
          <CreatePassword />
        </Container>
      )
    }

    if (needNonce) {
      return (
        <Container maxWidth={920} width="100%" padding={["0 10px", "40px 0"]} margin={'0 auto'}>
          <PackTitle />
          <PromptForPassword />
        </Container>
      )
    }

    if (needOldPassword(pack, user)) {
      return (
        <Container maxWidth={920} width="100%" padding={["0 10px", "40px 0"]} margin={'0 auto'}>
          <PackTitle />
          <PromptForOldPassword />
        </Container>
      )
    }
  }

  const basePack: JSX.Element = (
    <Container maxWidth={920} width="100%" padding={["0 10px", "40px 0"]} margin={'0 auto'}>
      <PackTitle />
      <ItemDnD />
      <CreatorCard />
      <AddAssetFooter />
      {displayAd && (
        <Ad />
      )}
    </Container>
  );

  const withEditablePack = (pack: JSX.Element): JSX.Element => (
    <DraftAssetWrapper>
      {pack}
    </DraftAssetWrapper>
  );

  const withReadOnlyPack = (pack: JSX.Element): JSX.Element => (
    <>
      <LoginPrompt />
      {pack}
    </>
  );

  return (
    <AssetSelectionProvider>
      {isPackReadOnly ? withReadOnlyPack(basePack) : withEditablePack(basePack)}
    </AssetSelectionProvider>
  );
}

SinglePack.getLayout = getPackLayout;
SinglePack.allowedRoles = [RoleLevels.MEMBER, RoleLevels.ADMIN, RoleLevels.OWNER];

export default SinglePack;
